import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  Globe,
  Server,
  Cloud,
  ShieldCheck,
  Activity,
  Printer,
} from "lucide-react"

import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { adaptiveEcosystemsFAQs } from "@/data/faqData"

export default function AdaptiveEcosystems() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEO
        title="Adaptive Ecosystems | Resilient Cloud Infrastructure | RSA Tech Group"
        description="Building resilient, auto-scaling infrastructure that powers high-velocity digital transformation. Self-healing and AI-driven."
        image="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=2600"
      />
      <Navigation />

      {/* Print/Download Button */}
      <button
        onClick={() => window.print()}
        className="fixed top-32 right-6 z-50 bg-gradient-to-r from-blue-600 to-orange-500 text-white px-4 py-2 rounded-full hover:scale-105 transition-all flex items-center gap-2 print:hidden shadow-xl shadow-blue-900/20 group animate-fade-in"
        title="Save Case Study as PDF"
      >
        <Printer className="w-4 h-4" />
        <span className="text-sm font-bold hidden md:inline">Save as PDF</span>
      </button>

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)] opacity-30 pointer-events-none" />
        <div className="section-container relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-8">
            Adaptive <span className="text-accent">Ecosystems</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            Building resilient, auto-scaling infrastructure that powers
            high-velocity digital transformation.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Infrastructure That Breathes
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Traditional static infrastructure limits agility. We architect
                adaptive cloud ecosystems that dynamically scale resources based
                on real-time demand, ensuring peak performance and optimal
                cost-efficiency.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our ecosystems are self-healing and resilient, using AI to
                predict outages and reroute traffic before users are impacted.
              </p>

              <div className="grid grid-cols-1 gap-4 mb-8">
                {[
                  { icon: Cloud, text: "Cloud-Native Architecture" },
                  { icon: Activity, text: "Auto-Scaling & Self-Healing" },
                  { icon: Server, text: "Kubernetes Orchestration" },
                  { icon: ShieldCheck, text: "Zero Trust Security" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center text-primary font-medium p-4 bg-white rounded-lg shadow-sm border border-gray-100"
                  >
                    <div className="p-2 bg-accent/10 rounded-md mr-4">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-accent/10 rounded-lg transform translate-x-4 translate-y-4" />
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=2600"
                alt="Global Network Connection"
                className="rounded-lg shadow-xl relative z-10 w-full object-cover h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-primary mb-6">
              Resilient Core
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Hybrid Cloud",
                desc: "Seamlessly integrate on-premise security with public cloud scalability.",
                img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8",
              },
              {
                title: "DevSecOps",
                desc: "Automated security pipelines that ensure rapid, safe code deployment.",
                img: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&q=80&w=800",
              },
              {
                title: "Edge Computing",
                desc: "Pushing processing power to the network edge for ultra-low latency.",
                img: "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?auto=format&fit=crop&q=80&w=800",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="group rounded-xl overflow-hidden shadow-lg border border-gray-100"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={card.img + "?auto=format&fit=crop&q=80&w=800"}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-primary mb-3">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8"
              >
                Scale Your Infrastructure{" "}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PageFAQ
        faqs={adaptiveEcosystemsFAQs}
        title="Adaptive Ecosystems FAQs"
        subtitle="Common questions about cloud infrastructure and self-healing systems"
        variant="light"
      />

      <Footer />
    </div>
  )
}
